/**
 * Overview Controller
 *
 * Loads usage/cost/agent/provider data for the redesigned Overview dashboard.
 * Calls existing RPCs in parallel: usage.cost, sessions.usage, usage.status,
 * igloobot.stats, agents.list.
 */

import type { GatewayBrowserClient } from "../gateway.ts";
import type {
  CostUsageSummary,
  SessionsUsageResult,
  SessionsUsageTotals,
} from "../types.ts";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ProviderPlanWindow = {
  label: string;
  usedPercent: number;
  resetAt?: number;
};

export type ProviderPlanEntry = {
  provider: string;
  displayName: string;
  plan?: string;
  windows: ProviderPlanWindow[];
  error?: string;
};

export type AgentUsageEntry = {
  id: string;
  name?: string;
  model?: string;
  fallbacks?: string[];
  tokensToday: number;
  costToday: number;
  messagesToday: number;
  lastActive: number | null;
};

export type DailyTokenEntry = {
  date: string;
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
  total: number;
};

export type DailyCostEntry = {
  date: string;
  cost: number;
};

export type ProviderBreakdownEntry = {
  provider: string;
  model?: string;
  tokens: number;
  cost: number;
  count: number;
  percent: number;
};

export type OverviewDataState = {
  client: GatewayBrowserClient | null;
  connected: boolean;

  // Loading + error
  overviewLoading: boolean;
  overviewError: string | null;

  // Selected time range
  overviewTimeRange: TimeRange;
  overviewChartLabel: string;

  // Hero metrics (today)
  tokensToday: number;
  tokensInput: number;
  tokensOutput: number;
  costToday: number;
  cacheHitRate: number;
  activeSessions: number;
  // Trends (vs yesterday, -100 to +inf, NaN if no data)
  tokensTrend: number;
  costTrend: number;

  // Time series (7-day)
  dailyTokens: DailyTokenEntry[];
  dailyCost: DailyCostEntry[];

  // Per-provider breakdown (today)
  byProvider: ProviderBreakdownEntry[];

  // Per-agent usage (today)
  agentUsage: AgentUsageEntry[];

  // Provider plan usage
  providerPlans: ProviderPlanEntry[];

  // System stats (existing, compact)
  systemStats: {
    projects: number;
    users: number;
    tasks: number;
    messages: number;
    sessions: number;
    avatarSessions: number;
    skills: number;
    n8nWorkflows: number;
    cronJobs: number;
    documents: number;
    emailConfigured: boolean;
  } | null;

  // Gateway info
  uptime: string;
  gatewayConnected: boolean;
};

// ---------------------------------------------------------------------------
// State initializer
// ---------------------------------------------------------------------------

export function createOverviewDataState(): OverviewDataState {
  return {
    client: null,
    connected: false,
    overviewLoading: false,
    overviewError: null,
    overviewTimeRange: "today",
    overviewChartLabel: "Today",
    tokensToday: 0,
    tokensInput: 0,
    tokensOutput: 0,
    costToday: 0,
    cacheHitRate: 0,
    activeSessions: 0,
    tokensTrend: 0,
    costTrend: 0,
    dailyTokens: [],
    dailyCost: [],
    byProvider: [],
    agentUsage: [],
    providerPlans: [],
    systemStats: null,
    uptime: "n/a",
    gatewayConnected: false,
  };
}

// ---------------------------------------------------------------------------
// Data loading
// ---------------------------------------------------------------------------

type StatsResult = {
  ok: boolean;
  stats: OverviewDataState["systemStats"];
};

type ProviderUsageSummary = {
  updatedAt: number;
  providers: ProviderPlanEntry[];
};

type AgentsListResult = {
  agents: Array<{
    id: string;
    name?: string;
    model?: string | { primary?: string; fallbacks?: string[] };
    status?: string;
    lastActiveAt?: number;
  }>;
};

// ---------------------------------------------------------------------------
// Time range types
// ---------------------------------------------------------------------------

export type TimeRange =
  | "today"
  | "yesterday"
  | "7d"
  | "30d"
  | "month"
  | "year"
  | "365d";

export const TIME_RANGE_LABELS: Record<TimeRange, string> = {
  today: "Today",
  yesterday: "Yesterday",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  month: "Current month",
  year: "Current year",
  "365d": "Past year",
};

export const TIME_RANGE_OPTIONS: Array<{ value: TimeRange; label: string }> = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "month", label: "Current month" },
  { value: "year", label: "Current year" },
  { value: "365d", label: "Past year" },
];

function dateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function todayStr(): string {
  return dateStr(new Date());
}

function daysAgoStr(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return dateStr(d);
}

/** Compute (start, end, prevStart, prevEnd) for a given time range. */
function resolveTimeRange(range: TimeRange): {
  start: string;
  end: string;
  prevStart: string;
  prevEnd: string;
  chartLabel: string;
} {
  const now = new Date();
  const today = dateStr(now);

  switch (range) {
    case "today": {
      const y = new Date();
      y.setDate(y.getDate() - 1);
      const ys = dateStr(y);
      return { start: today, end: today, prevStart: ys, prevEnd: ys, chartLabel: "Today" };
    }
    case "yesterday": {
      const y = new Date();
      y.setDate(y.getDate() - 1);
      const ys = dateStr(y);
      const beforeY = new Date();
      beforeY.setDate(beforeY.getDate() - 2);
      return { start: ys, end: ys, prevStart: dateStr(beforeY), prevEnd: dateStr(beforeY), chartLabel: "Yesterday" };
    }
    case "7d": {
      return { start: daysAgoStr(6), end: today, prevStart: daysAgoStr(13), prevEnd: daysAgoStr(7), chartLabel: "Last 7 days" };
    }
    case "30d": {
      return { start: daysAgoStr(29), end: today, prevStart: daysAgoStr(59), prevEnd: daysAgoStr(30), chartLabel: "Last 30 days" };
    }
    case "month": {
      const monthStart = dateStr(new Date(now.getFullYear(), now.getMonth(), 1));
      const prevMonthStart = dateStr(new Date(now.getFullYear(), now.getMonth() - 1, 1));
      const prevMonthEnd = dateStr(new Date(now.getFullYear(), now.getMonth(), 0));
      return { start: monthStart, end: today, prevStart: prevMonthStart, prevEnd: prevMonthEnd, chartLabel: now.toLocaleString("default", { month: "long" }) };
    }
    case "year": {
      const yearStart = `${now.getFullYear()}-01-01`;
      const prevYearStart = `${now.getFullYear() - 1}-01-01`;
      const prevYearEnd = `${now.getFullYear() - 1}-12-31`;
      return { start: yearStart, end: today, prevStart: prevYearStart, prevEnd: prevYearEnd, chartLabel: String(now.getFullYear()) };
    }
    case "365d": {
      return { start: daysAgoStr(364), end: today, prevStart: daysAgoStr(729), prevEnd: daysAgoStr(365), chartLabel: "Past year" };
    }
  }
}

function computeTrend(today: number, yesterday: number): number {
  // Growth from a zero baseline has no meaningful percentage (infinite), and
  // capping it at a flat 100% understates real spikes. Return NaN so the UI
  // shows no trend indicator (formatTrend renders "" for NaN) rather than a
  // misleading number. Both zero → no change.
  if (yesterday === 0) return today > 0 ? Number.NaN : 0;
  return Math.round(((today - yesterday) / yesterday) * 100);
}

export async function loadOverviewData(state: OverviewDataState): Promise<void> {
  if (!state.client || !state.connected) return;
  if (state.overviewLoading) return;

  state.overviewLoading = true;
  state.overviewError = null;

  try {
    const range = resolveTimeRange(state.overviewTimeRange);
    state.overviewChartLabel = range.chartLabel;

    // Fire all RPCs in parallel
    const [costRange, sessionsRange, sessionsPrev, providerUsage, statsRes, agentsRes] =
      await Promise.all([
        state.client.request<CostUsageSummary>("usage.cost", {
          startDate: range.start,
          endDate: range.end,
        }).catch(() => null),

        state.client.request<SessionsUsageResult>("sessions.usage", {
          startDate: range.start,
          endDate: range.end,
          limit: 500,
          includeContextWeight: false,
        }).catch(() => null),

        state.client.request<SessionsUsageResult>("sessions.usage", {
          startDate: range.prevStart,
          endDate: range.prevEnd,
          limit: 500,
          includeContextWeight: false,
        }).catch(() => null),

        state.client.request<ProviderUsageSummary>("usage.status", {}).catch(() => null),

        state.client.request<StatsResult>("igloobot.stats", {}).catch(() => null),

        state.client.request<AgentsListResult>("agents.list", {}).catch(() => null),
      ]);

    // --- Daily time series (for the selected range) ---
    if (costRange?.daily) {
      state.dailyTokens = costRange.daily.map((d) => ({
        date: d.date,
        input: d.input,
        output: d.output,
        cacheRead: d.cacheRead,
        cacheWrite: d.cacheWrite,
        total: d.totalTokens,
      }));
      state.dailyCost = costRange.daily.map((d) => ({
        date: d.date,
        cost: d.totalCost,
      }));
    }

    // --- Hero metrics (for the selected range) ---
    const rangeTotals = sessionsRange?.totals;
    const prevTotals = sessionsPrev?.totals;

    state.tokensToday = rangeTotals?.totalTokens ?? 0;
    state.tokensInput = rangeTotals?.input ?? 0;
    state.tokensOutput = rangeTotals?.output ?? 0;
    state.costToday = rangeTotals?.totalCost ?? 0;
    state.tokensTrend = computeTrend(
      rangeTotals?.totalTokens ?? 0,
      prevTotals?.totalTokens ?? 0,
    );
    state.costTrend = computeTrend(
      rangeTotals?.totalCost ?? 0,
      prevTotals?.totalCost ?? 0,
    );

    const totalInput = rangeTotals?.input ?? 0;
    const cacheRead = rangeTotals?.cacheRead ?? 0;
    state.cacheHitRate = totalInput + cacheRead > 0
      ? cacheRead / (totalInput + cacheRead)
      : 0;

    state.activeSessions = sessionsRange?.sessions?.length ?? 0;

    // --- Per-provider breakdown ---
    if (sessionsRange?.aggregates?.byProvider) {
      const grandTotal = sessionsRange.aggregates.byProvider.reduce(
        (sum, p) => sum + (p.totals.totalTokens ?? 0),
        0,
      );
      state.byProvider = sessionsRange.aggregates.byProvider.map((p) => ({
        provider: p.provider ?? "unknown",
        model: p.model,
        tokens: p.totals.totalTokens ?? 0,
        cost: p.totals.totalCost ?? 0,
        count: p.count,
        percent: grandTotal > 0 ? Math.round(((p.totals.totalTokens ?? 0) / grandTotal) * 100) : 0,
      }));
    }

    // --- Per-agent usage ---
    const agentAgg = sessionsRange?.aggregates?.byAgent ?? [];
    const agentList = agentsRes?.agents ?? [];
    state.agentUsage = agentList.map((agent) => {
      const agg = agentAgg.find((a) => a.agentId === agent.id);
      const model = typeof agent.model === "string"
        ? agent.model
        : agent.model?.primary;
      const fallbacks = typeof agent.model === "object" && agent.model?.fallbacks
        ? agent.model.fallbacks
        : undefined;
      return {
        id: agent.id,
        name: agent.name,
        model,
        fallbacks,
        tokensToday: agg?.totals.totalTokens ?? 0,
        costToday: agg?.totals.totalCost ?? 0,
        messagesToday: 0,
        lastActive: agent.lastActiveAt ?? null,
      };
    });

    // If agents.list didn't return agents, fall back to aggregate-only
    if (state.agentUsage.length === 0 && agentAgg.length > 0) {
      state.agentUsage = agentAgg.map((a) => ({
        id: a.agentId,
        tokensToday: a.totals.totalTokens ?? 0,
        costToday: a.totals.totalCost ?? 0,
        messagesToday: 0,
        lastActive: null,
      }));
    }

    // --- Provider plan usage ---
    if (providerUsage?.providers) {
      state.providerPlans = providerUsage.providers;
    }

    // --- System stats ---
    if (statsRes?.ok && statsRes.stats) {
      state.systemStats = statsRes.stats;
    }
  } catch (err) {
    state.overviewError = err instanceof Error ? err.message : String(err);
  } finally {
    state.overviewLoading = false;
  }
}
